import { getUiLang } from "app/i18n/i18n";
import { getAggregations } from "pages/redesign/publications/api/searchRequest/getAggregations";
import { useGetPreferences } from "api/preferences/useGetPreferences";
import { PUB_LANG } from "redux/slices/api/initialisePreferences";
import {
  getAssetClassFilters,
  getTermFilters,
} from "../../../pages/redesign/publications/api/searchRequest/useGetFilters";

export const generateTagsRequest = (filters) => ({
  query: {
    q: [],
    title: [],
  },
  filters,
  dates: {},
  aggregations: getAggregations(),
  size: 5,
  from: 0,
  sort: {
    publicationDate: {
      order: "desc",
    },
  },
});

const getPubKeyToStratusMap = (lang) => ({
  companies: `companies.name.${lang}`,
  periodicals: `reportTitles.${lang}`,
  primaryPeriodicals: `reportTitles.${lang}`,
  analysts: `analysts.name.${lang}`,
  gicsIndustryGroups: `gicsIndustryGroups.${lang}`,
  gicsSectors: `gicsSectors.${lang}`,
  topixSectors: `topixSectors.${lang}`,
  txSubCategories: `txSubCategories.${lang}`,
  txCategories: `txCategories.en`,
  reportTypes: `reportTypes.${lang}`,
  reportTitles: `reportTitles.${lang}`,
});

export function useCreateFilterRequest(
  selectedSubscriptionTags,
  qs,
  selectedLanguages,
  selectedPub,
  isSubscriptionFromPillsBar,
) {
  const prefLang = useGetPreferences(PUB_LANG);
  const languageFromPreferences = selectedLanguages?.length ? selectedLanguages : prefLang.length ? prefLang : ["en"];
  const finalLang = isSubscriptionFromPillsBar ? languageFromPreferences : [selectedPub?.language];

  if (isSubscriptionFromPillsBar) {
    const selectedValuesByKey = (selectedSubscriptionTags || [])
      .filter((t) => t?.selected === true)
      .reduce((acc, { key, tag }) => {
        if (!acc[key]) acc[key] = new Set();
        acc[key].add(tag);
        return acc;
      }, {});

    const filtersSets = { language: new Set(finalLang) };

    const assetFilters = getAssetClassFilters(qs);
    const filteredQueryString = getTermFilters(qs, finalLang);
    const finalQueryString = { ...filteredQueryString, ...assetFilters };

    Object.entries(finalQueryString || {}).forEach(([stratusKey, value]) => {
      let domainKey;
      if (stratusKey === "companies.nameWithTicker.en" || stratusKey === "companies.nameWithTicker.ja") {
        domainKey = "companyWithTicker";
      } else if (stratusKey === "companies.securityType") {
        domainKey = "companySecurityType";
      } else {
        domainKey = stratusKey.split(".")[0];
      }

      const selectedSet = selectedValuesByKey[domainKey];
      if (!selectedSet) return;

      const values = Array.isArray(value) ? value : [value];
      const allowed = values.filter((v) => selectedSet.has(v));
      if (!allowed.length) return;

      if (!filtersSets[stratusKey]) filtersSets[stratusKey] = new Set();
      allowed.forEach((v) => filtersSets[stratusKey].add(v));
    });

    const filters = {};
    Object.entries(filtersSets).forEach(([k, set]) => {
      filters[k] = Array.from(set);
    });

    return filters;
  }

  return selectedSubscriptionTags.reduce(
    (acc, { key, selected, tag }) => {
      const lang = getUiLang();
      const tagKey = getPubKeyToStratusMap(lang)[key];

      if (selected && tagKey) {
        if (!acc[tagKey]) {
          acc[tagKey] = [];
        }
        acc[tagKey].push(tag);
      }

      return acc;
    },
    { language: finalLang },
  );
}

const formatDate = (uiLang) =>
  new Intl.DateTimeFormat(uiLang, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });

export function formatPublicationDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return formatDate(getUiLang()).format(d);
}

export function injectTxCatFilters(filters, selectedPub, isSubscriptionFromPillsBar, selectedSubscriptionTags) {
  if (isSubscriptionFromPillsBar) return filters;

  const txCatsArray = filterTxCategory(selectedPub, selectedSubscriptionTags);

  return {
    ...filters,
    ...(txCatsArray.length > 0 ? { "txCategories.en": txCatsArray } : null),
  };
}

function filterTxCategory(selectedPub, selectedSubscriptionTags) {
  const txCategories = Array.isArray(selectedPub?.txCategories) ? selectedPub.txCategories : [];
  const unselectedTxCategories = new Set(
    Array.isArray(selectedSubscriptionTags)
      ? selectedSubscriptionTags
          .filter(({ key, selected }) => key === "txCategories" && !selected)
          .map(({ tag }) => tag)
      : [],
  );

  return txCategories
    .filter((cat) => !unselectedTxCategories.has(cat.ja) && !unselectedTxCategories.has(cat.en))
    .map((cat) => cat.en);
}
